// Currency formatting for the invoice surfaces (F37 §12 redesign, Task 5;
// localised by the 2026-07-28 design review, finding 5).
//
// Vendors are mixed-currency (Cloudflare/Microsoft bill in foreign
// currency), so the symbol is currency-driven: AOA → "Kz", USD → "$",
// EUR → "€"; anything else falls back to the ISO code. Universal — no
// per-tenant currency assumption baked in.
//
// The GROUPING is language-driven, not currency-driven: an Angolan reading
// a USD invoice in a PT-AO tab still reads `12 500,00`, and the same tab in
// EN reads `12,500.00`. The review's finding was that the dashboard printed
// the backend's raw decimal string (`Kz 12500.00`) while the weekly digest
// email localised the same amount — two surfaces, one number, two spellings,
// in an accounting product where `12500.00` and `1250.00` are one glance
// apart in a right-aligned column.

import type { Language } from "./i18n";

const SYMBOLS: Record<string, string> = {
  AOA: "Kz",
  USD: "$",
  EUR: "€",
};

/** Per-language separators. PT-AO groups with a NO-BREAK SPACE (U+00A0):
 * non-breaking so a thousands group can never wrap mid-number, and full
 * width so the gap is actually visible — U+202F renders near-zero in
 * Manrope, which puts the grouped figure back to looking ungrouped and
 * defeats the point. */
const SEPARATORS: Record<Language, { group: string; decimal: string }> = {
  "pt-AO": { group: " ", decimal: "," },
  en: { group: ",", decimal: "." },
};

export interface MoneyParts {
  /** Currency symbol or ISO code — rendered muted. */
  symbol: string;
  /** Grouped, localised amount — rendered bold + tabular. */
  value: string;
}

/** Split a Decimal-serialised amount into its integer + fraction halves
 * without going through a float (precision is the whole reason the backend
 * sends a string). Tolerates a missing fraction and a leading sign. */
function splitDecimal(amount: string): {
  sign: string;
  whole: string;
  fraction: string;
} {
  const trimmed = amount.trim();
  const sign = trimmed.startsWith("-") ? "-" : "";
  const unsigned = sign ? trimmed.slice(1) : trimmed;
  const [whole = "0", fraction = ""] = unsigned.split(".");
  return { sign, whole, fraction };
}

/** Group an integer string in threes from the right. */
function group(whole: string, separator: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/** Format a `{ amount, currency }` pair (backend `Money`) into its two
 * display halves. `amount` arrives as a string (Decimal-serialised) so we
 * never lose precision to a float. The fraction is padded to two places —
 * an accounting surface shows `12 500,00`, not `12 500,0`. */
export function formatMoneyParts(
  money: { amount: string; currency: string } | null | undefined,
  language: Language,
): MoneyParts | null {
  if (!money) return null;
  const { group: g, decimal } = SEPARATORS[language] ?? SEPARATORS["pt-AO"];
  const { sign, whole, fraction } = splitDecimal(money.amount);
  // Non-numeric payload (a backend that grew a new shape): show it raw
  // rather than mangling it into a plausible-looking wrong number.
  if (!/^\d+$/.test(whole)) {
    return { symbol: SYMBOLS[money.currency] ?? money.currency, value: money.amount };
  }
  const padded = (fraction + "00").slice(0, 2);
  return {
    symbol: SYMBOLS[money.currency] ?? money.currency,
    value: `${sign}${group(whole, g)}${decimal}${padded}`,
  };
}

/** Single-string form for the surfaces that render money inline (detail
 * rows, activity sentences). Returns the em-dash placeholder for a null
 * amount, matching the rest of the "no value" vocabulary. */
export function formatMoney(
  money: { amount: string; currency: string } | null | undefined,
  language: Language,
): string {
  const parts = formatMoneyParts(money, language);
  return parts ? `${parts.symbol} ${parts.value}` : "—";
}
